import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Download, RefreshCw } from '../../icons';
import {
  Badge, Button, EmptyState, PageHeader, Select, Table,
  type TableColumn,
  StatTile,
  StatTiles
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import { listLeaveSettlements, type SettlementRowDto } from '../../api/hr-admin';
import { settlementCsv, basisDiffers, wonText, RESIGN_KIND_LABEL, BASIS_LABEL } from '../../lib/leave-settlement';
import { getNumberLocale } from '../../lib/base-i18n';
import './AttendanceAdmin.css';
import './LeaveSettlement.css';

/**
 * **퇴사 정산** — 판 J 넷째 조각 (2026-08-30).
 *
 * ## 무엇이 없었나
 *
 * 오프보딩 체크리스트에 「연차 정산」이 **필수 항목**으로 있었는데, **누를 곳만 있고 계산이
 * 없었다.** 며칠을 정산해야 하는지, 얼마인지 아무 데도 안 나온다 — 체크 표시는 「했다」는
 * 말일 뿐 **무엇을 했는지는 남지 않는다.**
 *
 * ## ★★ 두 기준을 나란히 보여 준다
 *
 * 회계연도 기준으로 연차를 주는 회사도 **퇴직 시점에는 입사일 기준으로 다시 계산해서**
 * 근로자에게 불리하지 않게 지급해야 한다. 그래서 이 표는 **회사 규칙**과 **입사일 기준**을
 * 둘 다 적고, 갈린 사람에게는 그 사실을 표시한다. 하나만 보이면 나중에 근로자가 물었을 때
 * 답할 근거가 없다.
 *
 * ## 여기서 지급하지 않는다
 *
 * 실제 지급은 급여의 몫이다. 이 화면은 **얼마를 넘길지 계산해 보여 주고 CSV 로 넘기는 것**
 * 까지만 한다 — 지급 사실을 여기에도 적으면 급여와 둘로 갈라진다.
 *
 * ── 정본 채택 (2026-09-06 · 채택 3단계 ③ 근태 · 연차 넷) ─────────────────────────
 * 손으로 짠 머리 · 왼쪽 규칙 칸 · 표를 정본으로 옮겼다 — `PageHeader` · `Select` ·
 * `Table`(rowHeader) · `Badge` · `EmptyState` · `Button`.
 *
 * ★★ **왼쪽 규칙 칸은 통째로 도움말로 갔다**(정본 P8). 「두 기준을 견준다」 · 「부여를 언제로
 *    세나」 · 「예상 수당 = …」 · 「여기서 지급하지 않는다」는 **늘 같은 말**이라, 정산을 백 번
 *    열어도 백 번 같은 글이 뜬다 — 매번 눈에 들어오면서 아무 정보도 새로 주지 않는 자리다.
 * ★★ 반대로 **화면에 남긴 셋**은 값에 따라 달라진다:
 *    「퇴사 처리를 시작한 사람만 나옵니다」(이 목록이 무엇인가) · 요약 넷 ·
 *    **「임금이 없어 금액을 못 낸 사람 N명」**(0원으로 적으면 못 준 돈이 조용히 사라진다).
 * ★ 요약 넷(`hra__stats`)은 **그대로 뒀다** — 정본에 「요약 타일」 패턴이 아직 없다.
 *   지어내지 않는다(#958 과 같은 판단).
 */

const ERRORS: Record<string, string> = {
  FORBIDDEN: '이 화면은 근태 담당자만 쓸 수 있습니다.',
};

type Filter = 'ALL' | 'OPEN' | 'DONE';

const FILTERS: ReadonlyArray<{ value: Filter; label: string }> = [
  { value: 'ALL', label: '전체' },
  { value: 'OPEN', label: '진행 중' },
  { value: 'DONE', label: '확정' },
];

/** 한 기준의 셈을 한 칸에 — 굵게 «미사용», 그 아래 회차와 부여·사용. */
function BasisCell({ b }: { b: SettlementRowDto['company'] }): JSX.Element {
  if (!b) return <span className="hra__dim">회차 없음</span>;
  return (
    <div className="lvst__basis">
      <b>미사용 {b.unused}일</b>
      <span className="hra__dim">
        {b.periodStart} ~ {b.periodEnd} · 부여 {b.granted} · 사용 {b.used}
      </span>
    </div>
  );
}

export function LeaveSettlementPage(): JSX.Element {
  const [rows, setRows] = useState<SettlementRowDto[]>([]);
  const [filter, setFilter] = useState<Filter>('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listLeaveSettlements(filter === 'ALL' ? undefined : filter);
      setRows(res.rows);
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(ERRORS[code ?? ''] ?? '불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { void load(); }, [load]);

  const totals = useMemo(() => {
    let days = 0;
    let pay = 0;
    let unknown = 0;
    for (const r of rows) {
      days += r.settleDays ?? 0;
      if (r.estimatedPay === null) unknown += 1;
      else pay += r.estimatedPay;
    }
    return { days: Math.round(days * 10) / 10, pay, unknown, differ: rows.filter(basisDiffers).length };
  }, [rows]);

  const download = () => {
    const blob = new Blob([settlementCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '퇴사정산.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: ReadonlyArray<TableColumn<SettlementRowDto>> = useMemo(() => [
    {
      key: 'name',
      header: '이름',
      width: '170px',
      // ★ 이름 칸은 **그 행의 이름**이다 — `<th scope="row">` 라야 읽어 주는 쪽에서
      //   「이정우의 예상 수당」으로 들린다(정본 E1 `rowHeader`).
      rowHeader: true,
      render: (r) => (
        <>
          {r.name}
          <span className="hra__dim"> · {RESIGN_KIND_LABEL[r.resignKind] ?? r.resignKind}</span>
          {r.status === 'DONE' && <Badge size="sm">확정</Badge>}
        </>
      ),
    },
    {
      key: 'resignDate',
      header: '퇴사일',
      width: '160px',
      render: (r) => (
        <>
          {r.resignDate}
          <span className="hra__dim"> · 입사 {r.hireDate ?? '미상'}</span>
        </>
      ),
    },
    { key: 'company', header: '회사 규칙', render: (r) => <BasisCell b={r.company} /> },
    { key: 'statutory', header: '입사일 기준', render: (r) => <BasisCell b={r.statutory} /> },
    {
      key: 'settle',
      header: '정산 일수',
      width: '150px',
      render: (r) => (
        <span className="is-strong">
          {r.settleDays === null ? '—' : `${r.settleDays}일`}
          {r.settleBasis && (
            // ★ 갈린 사람은 눈에 들어와야 한다 — 왜 이 숫자인지 **설명해야 할 사람**이다.
            <Badge variant={basisDiffers(r) ? 'warning' : 'default'} size="sm">
              {`${BASIS_LABEL[r.settleBasis] ?? r.settleBasis}${basisDiffers(r) ? ' · 갈림' : ''}`}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'substitute',
      header: '보상휴가',
      width: '92px',
      render: (r) => (
        <span className={r.substituteRemain > 0 ? undefined : 'is-muted'}>
          {r.substituteRemain > 0 ? `${r.substituteRemain}일` : '—'}
        </span>
      ),
    },
    {
      key: 'pay',
      header: '예상 수당',
      width: '128px',
      align: 'right',
      render: (r) => (
        <span className={r.estimatedPay === null ? 'is-warn' : 'is-strong'}>{wonText(r.estimatedPay)}</span>
      ),
    },
  ], []);

  return (
    <div className="hra">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/leave-settlement')}
        scope={scopeFor('/erp/attendance/leave-settlement')}
        title="퇴사 정산"
        description="퇴사 처리를 시작한 사람만 나옵니다 — 인사 › 퇴사 처리에서 시작합니다."
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p><b>두 기준을 견줍니다.</b> 회사 규칙(연차정책에 정한 회계연도 또는 입사일)과
                <b> 입사일 기준</b>(법이 보는 기준)을 나란히 셈하고, 둘 중 <b>미사용이 많은 쪽</b>을
                정산 대상으로 둡니다. 회계연도 기준 회사도 퇴직 때는 입사일 기준으로 다시 셈해
                근로자에게 불리하지 않게 지급해야 합니다.</p>
              <p><b>부여를 언제로 세나</b> — 1년 이상 회차는 <b>회차 시작일</b>(그날 이미 발생한
                몫입니다), 1년 미만 회차는 <b>퇴사일까지</b>(개근한 달마다 쌓입니다).</p>
              <p><b>예상 수당</b> = 미사용일수 × 1일 소정근로시간 × 통상시급. 임금이 등록되지 않은
                사람은 <b>금액을 내지 않습니다</b> — 0원으로 적으면 못 준 돈이 조용히 사라집니다.</p>
              <p><b>여기서 지급하지는 않습니다.</b> 실제 지급은 급여의 몫이고, 이 화면은 넘길 값을
                계산해 CSV 로 건네는 데까지입니다. 지급 사실을 여기에도 적으면 급여와 둘로 갈라집니다.</p>
              <p><b>보상휴가 잔여는 연차와 다른 통</b>이라 합치지 않습니다 — 급여가 따로 셈합니다.</p>
            </>
          ),
        }}
        actions={(
          <Select
            ariaLabel="상태 고르기"
            size="sm"
            value={filter}
            onChange={(v) => setFilter(v as Filter)}
            options={FILTERS}
          />
        )}
      />

      {/* 정본 D9-2 요약 타일 (2026-09-06 신설) — 손짜기 `hra__stat` 에서 옮겨 왔다. */}
      <StatTiles ariaLabel="정산 현황">
        <StatTile label="대상" value={rows.length} unit="명" />
        <StatTile label="정산 일수 합계" value={totals.days} unit="일" />
        {/* ★ 단위를 라벨에서 꺼내 숫자에 붙인다 — 「(원)」이 뒤에 있으면 늦게 안다(정본 D9-2). */}
        <StatTile label="예상 수당" value={totals.pay.toLocaleString(getNumberLocale())} unit="원" />
        {/* ★ 왜 이 값인지 «설명해야 할 사람»이다 — 눈에 들어와야 한다. */}
        <StatTile label="기준이 갈린 사람" value={totals.differ} unit="명" tone={totals.differ > 0 ? 'warning' : 'default'} />
      </StatTiles>

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {/* ★★ 「몇 명이 빠졌나」는 **지금 이 표의 판정**이라 화면에 남는다(공식은 도움말로 갔다). */}
      {totals.unknown > 0 && (
        <p className="hra__error" role="status">
          <AlertCircle size={14} aria-hidden />
          임금이 등록되지 않아 금액을 못 낸 사람이 {totals.unknown}명 있습니다 — 합계에 안 들어갑니다.
        </p>
      )}

      <Table
        caption="퇴사 정산 대상"
        className="hra-tbl"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.offboardingId}
        loading={loading}
        empty={(
          <EmptyState
            title="퇴사 처리를 시작한 사람이 없습니다."
            description={filter === 'ALL'
              ? '인사 › 퇴사 처리에서 시작하면 여기에 나타납니다.'
              : '고른 상태에 해당하는 사람이 없습니다 — 「전체」로 돌아가 보세요.'}
            action={filter === 'ALL'
              ? undefined
              : <Button variant="secondary" size="sm" onClick={() => setFilter('ALL')}>전체 보기</Button>}
          />
        )}
      />

      <footer className="hra__foot">
        <div className="hra__actions">
          <Button variant="secondary" iconLeft={<RefreshCw size={14} aria-hidden />} onClick={() => { void load(); }} disabled={loading}>
            다시 불러오기
          </Button>
          <Button iconLeft={<Download size={14} aria-hidden />} onClick={download} disabled={rows.length === 0}>
            급여로 넘길 표 받기
          </Button>
        </div>
      </footer>
    </div>
  );
}

export default LeaveSettlementPage;
